import { useState, useRef, useEffect } from "react";
import { Search, X, MoreHorizontal, Check } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { cn } from "@/modern/utils";
import { Popover, PopoverTrigger, PopoverContent } from "@/modern/components/ui/popover";
import { useCoverage, type CoverageFilter } from "../context/CoverageContext";

const FILTERS: { id: CoverageFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "output_ready", label: "Output-ready" },
  { id: "partial", label: "Partial" },
  { id: "empty", label: "Needs work" },
  { id: "sequence_ready", label: "Sequence ready" },
];

function formatFilterCount(n: number) {
  if (n > 99) return "99+";
  return String(n);
}

// Space reserved for the search input + gaps (8.5rem ≈ 136px + 8px gap + 4px padding)
const SEARCH_RESERVED_PX = 148;

export const CoverageFilterBar = () => {
  const { activeFilter, setActiveFilter, searchQuery, setSearchQuery, filterCounts } =
    useCoverage();
  const [isCompact, setIsCompact] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Always in the DOM so we can measure its natural (scrollWidth) at any time
  const filtersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      const filters = filtersRef.current;
      if (!filters || !container) return;
      // Switch to compact the moment the pills + search would no longer fit
      setIsCompact(filters.scrollWidth + SEARCH_RESERVED_PX > container.clientWidth);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const activeFilterDef = FILTERS.find((f) => f.id === activeFilter) ?? FILTERS[0];

  const filterPills = (
    <div
      ref={filtersRef}
      className={cn(
        "flex items-center gap-1",
        // In compact mode: keep in DOM for measurement but hide from view
        isCompact && "pointer-events-none invisible absolute",
      )}
      aria-hidden={isCompact}
    >
      {FILTERS.map((f) => {
        const isActive = activeFilter === f.id;
        const count = filterCounts[f.id];
        return (
          <div key={f.id} className="relative shrink-0">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setActiveFilter(f.id)}
              aria-label={`${f.label}, ${count} hubs`}
              tabIndex={isCompact ? -1 : 0}
              className={cn(
                "h-8 rounded-full border px-3.5 text-xs shadow-none transition-[color,box-shadow,background-color,border-color]",
                "focus-visible:ring-2 focus-visible:ring-ring/45 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                isActive
                  ? "pointer-events-none border-transparent bg-primary/15 font-semibold text-foreground ring-1 ring-primary/20"
                  : "border-border/35 bg-card font-medium text-muted-foreground hover:border-border/55 hover:bg-accent/35 hover:text-foreground",
              )}
            >
              {f.label}
            </Button>
            <span
              className={cn(
                "pointer-events-none absolute -right-0.5 -top-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full border border-border/50 bg-muted/35 px-0.5 text-[10px] font-medium tabular-nums leading-none text-muted-foreground/90",
                count === 0 && "opacity-55",
              )}
              aria-hidden
            >
              {formatFilterCount(count)}
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div ref={containerRef} className="relative flex items-center gap-2 mb-5 px-0.5 pt-0.5">
      {/* Full pills — always measured; hidden when compact */}
      {filterPills}

      {/* Compact overlay — active pill + popover */}
      {isCompact && (
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="relative shrink-0">
            <span className="inline-flex h-8 items-center rounded-full border border-transparent bg-primary/15 px-3.5 text-xs font-semibold text-foreground ring-1 ring-primary/20">
              {activeFilterDef.label}
            </span>
            <span
              className={cn(
                "pointer-events-none absolute -right-0.5 -top-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full border border-border/50 bg-muted/35 px-0.5 text-[10px] font-medium tabular-nums leading-none text-muted-foreground/90",
                filterCounts[activeFilter] === 0 && "opacity-55",
              )}
              aria-hidden
            >
              {formatFilterCount(filterCounts[activeFilter])}
            </span>
          </div>

          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="h-8 w-8 shrink-0 rounded-full border border-border/35 bg-card p-0 text-muted-foreground hover:bg-accent/35 hover:text-foreground"
                aria-label="Filter options"
              >
                <MoreHorizontal className="size-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" sideOffset={6} className="w-48 p-1.5">
              <div className="flex flex-col gap-0.5">
                {FILTERS.map((f) => {
                  const isActive = activeFilter === f.id;
                  const count = filterCounts[f.id];
                  return (
                    <button
                      key={f.id}
                      onClick={() => {
                        setActiveFilter(f.id);
                        setPopoverOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-xs transition-colors",
                        isActive
                          ? "bg-primary/10 font-semibold text-foreground"
                          : "font-medium text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                      )}
                    >
                      <span className="flex items-center gap-2">
                        {isActive ? (
                          <Check className="size-3 shrink-0 text-primary" />
                        ) : (
                          <span className="size-3 shrink-0" />
                        )}
                        {f.label}
                      </span>
                      <span
                        className={cn(
                          "tabular-nums text-[10px] text-muted-foreground",
                          count === 0 && "opacity-50",
                        )}
                      >
                        {formatFilterCount(count)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      <div className="flex-1 min-w-0" />

      {/* Search — always visible */}
      <div className="relative shrink-0 w-[8.5rem]">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="text"
          placeholder="Search hubs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="background"
          inputSize="sm"
          className={cn(
            "h-8 w-full rounded-full border border-border/40 bg-card py-0 pl-8 text-xs leading-none text-card-foreground md:text-xs",
            "placeholder:text-muted-foreground",
            "!ring-0 !ring-offset-0 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/20",
            searchQuery ? "pr-8" : "pr-3.5",
          )}
        />
        {searchQuery ? (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  );
};
